import {
  Backdrop,
  Box,
  Button,
  CheckCircleIcon,
  Flex,
  Image,
  Spacer,
  Text,
  TextField,
  toaster,
  Tooltip,
} from 'components'
import { useState } from 'react'
import { forceDark, resetPassword, signIn, useBreakpoint, validEmail } from 'utils'
import { existingUser } from 'utils/auth'

export default function SignIn() {
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [resetRequired, setResetRequired] = useState(false)
  const isMobile = useBreakpoint() < 2

  function submit() {
    signIn(email.trim(), password).catch(handleError)
  }

  async function handleReset() {
    if (await existingUser(email)) {
      resetPassword(email.trim())
        .then(() => toaster.create({ title: 'Password reset email sent', type: 'success' }))
        .catch(handleError)
      setResetRequired(true)
    } else {
      toaster.create({ title: 'Could not find an account with that email', type: 'error' })
    }
  }

  function handleError(e: any) {
    let title = 'An error occurred'
    switch (e.code) {
      case 'auth/invalid-credential':
        title = 'Invalid credentials'
        break
      case 'auth/invalid-email':
        title = 'Invalid email address'
        break
      case 'auth/missing-email':
        title = 'Please enter an email address'
        break
      case 'auth/missing-password':
        title = 'Please enter a password'
        break
      case 'auth/too-many-requests':
        title = 'Too many attempts. Try again later'
        break
      default:
        console.error(e)
    }
    toaster.create({ title, type: 'error' })
  }

  return (
    <Backdrop
      variant="hero"
      flex={1}
      direction={{ base: 'column', lg: 'row' }}
      align={{ base: 'center', lg: 'stretch' }}
      justify={{ base: 'flex-start', lg: 'space-between' }}
      minH="100vh"
    >
      <HeroSection isMobile={isMobile} />
      <Flex
        align={{ base: 'flex-start', lg: 'center' }}
        flex={1}
        justify="center"
        px={{ base: 6, sm: 10 }}
        py={{ base: 8, lg: 14 }}
      >
        <Box
          {...forceDark}
          display="flex"
          bg="rgba(255, 255, 255, 0.08)"
          px={10}
          py={10}
          width={{ base: '100%', sm: '440px' }}
          maxWidth="100vw"
          borderRadius="24px"
          border="1px solid rgba(255, 255, 255, 0.12)"
          alignItems="flex-start"
          flexDirection="column"
          backdropFilter="blur(24px)"
          justifyContent="center"
        >
          {resetRequired ? (
            <Flex direction="column" gap={2} w="100%" align="center">
              <CheckCircleIcon fontSize="48px" type="outline" color="green.400" />
              <Text textStyle={{ base: 'h3', sm: 'h2' }} color="white">
                Email Sent
              </Text>
              <Text textStyle="bodySmall" color="rgba(255, 255, 255, 0.8)">
                Check your email and open the link we sent to continue.
              </Text>
            </Flex>
          ) : (
            <>
              <Flex direction="column" gap={2} mb={8}>
                <Text color="white" textStyle="h4">
                  Welcome!
                </Text>
                <Text color="rgba(255, 255, 255, 0.8)" fontSize="md">
                  Login using your Momentify account.
                </Text>
              </Flex>
              <Flex direction="column" alignSelf="stretch" gap={4}>
                <TextField
                  label="Email"
                  onChange={e => setEmail(e.target.value)}
                  placeholder="Enter email..."
                  type="email"
                  value={email}
                  variant="subtle"
                  border="1px solid rgba(255, 255, 255, 0.2)"
                />
                <TextField
                  label="Password"
                  onChange={e => setPassword(e.target.value)}
                  onKeyDown={e => e.key === 'Enter' && submit()}
                  placeholder="Enter password..."
                  type="password"
                  value={password}
                  variant="subtle"
                  border="1px solid rgba(255, 255, 255, 0.2)"
                />
                <Flex direction="row" width="100%">
                  <Button
                    disabled={!validEmail(email) || !password}
                    onClick={submit}
                    bg="white"
                    color="#17204A"
                    _hover={{ bg: 'rgba(255, 255, 255, 0.9)' }}
                  >
                    Log in
                  </Button>
                  <Spacer />
                  <Tooltip content={!validEmail(email) ? 'Enter a valid email' : undefined}>
                    <Button
                      variant="link"
                      color="rgba(255, 255, 255, 0.8)"
                      _hover={{ color: 'white' }}
                      disabled={!validEmail(email)}
                      onClick={() => handleReset()}
                    >
                      Forgot Password?
                    </Button>
                  </Tooltip>
                </Flex>
              </Flex>
            </>
          )}
        </Box>
      </Flex>
    </Backdrop>
  )
}

function HeroSection({ isMobile }: { isMobile: boolean }) {
  return (
    <Flex
      direction="column"
      justify={isMobile ? 'flex-start' : 'center'}
      px={{ base: 8, lg: 16 }}
      pt={{ base: 8, lg: 14 }}
      pb={{ base: 0, lg: 14 }}
      gap={{ base: 6, lg: 10 }}
      maxW={{ lg: '55%' }}
    >
      <Image
        src="/common/momentify-logo-reverse.svg"
        alt="Momentify"
        height="24px"
        alignSelf="flex-start"
      />
      <Flex direction="column" gap={3}>
        <Text
          color="white"
          fontSize={{ base: '4xl', lg: '6xl' }}
          fontWeight="semibold"
          letterSpacing="-1.2px"
          lineHeight="1.1"
        >
          Empower Every{' '}
          <Text
            as="span"
            css={{
              background: 'linear-gradient(135deg, #0cf4df, #5ba8f5)',
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent',
              backgroundClip: 'text',
            }}
          >
            Moment.
          </Text>
        </Text>
        <Text
          color="rgba(255, 255, 255, 0.85)"
          fontSize="15px"
          fontWeight="400"
          lineHeight="1.5"
          maxW="480px"
          opacity={1}
        >
          The operating system for in-person engagement.
        </Text>
      </Flex>
    </Flex>
  )
}
